// SPDX-License-Identifier: Elastic-2.0
/**
 * Install prompt UI (T-029).
 *
 * Two paths:
 *   - Android Chrome / desktop Chrome: `beforeinstallprompt` event fires.
 *     We capture it, gate it behind `dashboard-visit-count >= 3`, and
 *     show a bottom-sheet prompting the user to install. On accept we
 *     call the saved event's `prompt()`.
 *   - iOS Safari: no event. We detect via UA + standalone mode, then
 *     show a static instruction ("tap share → Add to Home Screen").
 *
 * Dismissal is remembered for 30 days via
 * `localStorage["install-prompt-dismissed-until"]`.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import styles from "./InstallPrompt.module.css";

const VISIT_COUNT_KEY = "dashboard-visit-count";
const DISMISSED_KEY = "install-prompt-dismissed-until";
const VISIT_THRESHOLD = 3;
const DISMISS_MS = 30 * 24 * 60 * 60 * 1000;

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function isIOSStandaloneEligible(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/.test(ua) && !("MSStream" in window);
  // `standalone` is iOS-only and absent in standalone (home-screen) mode.
  const inStandalone =
    "standalone" in navigator && (navigator as { standalone?: boolean }).standalone === true;
  return isIOS && !inStandalone;
}

function shouldShow(): boolean {
  try {
    const dismissedUntil = Number(localStorage.getItem(DISMISSED_KEY) ?? 0);
    if (dismissedUntil > Date.now()) return false;
    const visits = Number(localStorage.getItem(VISIT_COUNT_KEY) ?? 0);
    return visits >= VISIT_THRESHOLD;
  } catch {
    return false;
  }
}

export function InstallPrompt(): JSX.Element | null {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [iosVisible, setIosVisible] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onBeforeInstall = (e: Event): void => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setDeferred(promptEvent);
      if (shouldShow()) setOpen(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS fallback — no event to wait for; check eagerly.
    if (isIOSStandaloneEligible() && shouldShow()) setIosVisible(true);

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = (): void => {
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now() + DISMISS_MS));
    } catch {
      // Storage unavailable — drop silently; we just re-prompt next visit.
    }
    setOpen(false);
    setIosVisible(false);
  };

  const accept = async (): Promise<void> => {
    if (deferred) {
      try {
        await deferred.prompt();
        const result = await deferred.userChoice;
        if (result.outcome === "dismissed") dismiss();
      } finally {
        setDeferred(null);
        setOpen(false);
      }
    }
  };

  if (!open && !iosVisible) return null;

  return (
    <div className={styles.sheet} role="dialog" aria-live="polite" data-testid="install-prompt">
      <div className={styles.body}>
        <h2 className={styles.title}>{t("pwa.install_prompt_title")}</h2>
        <p className={styles.message}>
          {iosVisible ? t("pwa.install_prompt_ios_instruction") : t("pwa.install_prompt_body")}
        </p>
      </div>
      <div className={styles.actions}>
        <Button variant="ghost" size="sm" onClick={dismiss} data-testid="install-prompt-dismiss">
          {t("pwa.install_prompt_dismiss")}
        </Button>
        {!iosVisible && (
          <Button
            size="sm"
            onClick={() => {
              void accept();
            }}
            data-testid="install-prompt-install"
          >
            {t("pwa.install_prompt_install")}
          </Button>
        )}
      </div>
    </div>
  );
}
